// src/App.js

import React, { useContext, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import FileUpload from './FileUpload';
import ForgotPassword from './ForgotPassword';
import SignUp from './SignUp';
import Login from './Login';
import HomePage from './HomePage';
import ChatInterface from './ChatInterface';
import { AuthProvider, AuthContext } from './AuthContext';
import AdminPanel from './AdminPanel';
import IntegrationsPage from './pages/Admin/IntegrationsPage';
import AdminPlatformsPage from './pages/Admin/AdminPlatformsPage';
import OAuthCallback from './components/OAuthCallback';
import UserDetailPage from './UserDetailPage';
import UserFilesPage from './UserFilesPage';
import UserSettingsAdminPage from './UserSettingsAdminPage';
import SessionSettingsPage from './SessionSettingsPage';
import ResetPassword from './ResetPassword';
import PackagesAdmin from './PackagesAdmin';
import LiveAgentAdmin from './LiveAgentAdmin';
import AdminModels from './AdminModels';
import AdminEmbeddingModels from './AdminEmbeddingModels';
import AdminSystemDefaults from './AdminSystemDefaults';
import SystemHealthPage from './SystemHealthPage';
import AdminNotificationsPage from './AdminNotificationsPage';
import UserIntegrationsPage from './pages/Admin/Users/UserIntegrationsPage';
import { supabase } from './supabaseClient';
import CircularProgress from '@mui/material/CircularProgress'; // Ensure this is imported
import AcceptInvitationPage from './AcceptInvitationPage'; // Import the new page
import UserSettingsPage from './pages/Admin/Users/UserSettingsPage';

// Import new onboarding components
import UsageChoice from './UsageChoice';
import CreateCompanyForm from './CreateCompanyForm';
import CreateTeamForm from './CreateTeamForm';
import PricingPage from './PricingPage';
import PricingPublic from './PricingPublic';
import PaymentSuccess from './PaymentSuccess';
import PostBilling from './PostBilling';
import OnboardingProfile from './OnboardingProfile';
import OnboardingPreferences from './OnboardingPreferences';
import OnboardingWebsite from './OnboardingWebsite';
import OnboardingGoals from './OnboardingGoals';
import OnboardingTrial from './OnboardingTrial';
import BillingSuccessLanding from './BillingSuccessLanding';
import MyFilesPage from './MyFilesPage';
import UpgradePage from './UpgradePage';
import BillingPage from './BillingPage';
import FAQManagement from './FAQManagement';
import DemoAdmin from './DemoAdmin';
import DemoPage from './DemoPage';
import ProfilePage from './ProfilePage';
import WordPressOAuthCallback from './WordPressOAuthCallback';
import LeadManagementPage from './LeadManagementPage';
import LinkedInOutreachPage from './LinkedInOutreachPage';
import AgentInbox from './AgentInbox';
import WidgetSettings from './WidgetSettings';
import ChannelsSettings from './ChannelsSettings';
import DemoRequestedPage from './DemoRequestedPage';
import TestFormPage from './TestFormPage';

function PrivateRoute({ children }) {
  const { currentUser, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />;

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { currentUser, loading } = useContext(AuthContext);
  const location = useLocation();
  if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />;
  // If OAuth just redirected back with a code, wait here to allow session hydration
  if (typeof window !== 'undefined' && window.location.search.includes('code=')) {
    console.log('[PublicRoute] OAuth code present but no session yet; proceeding to children to allow login.');
    // Do not early-return spinner here; AuthContext handles the exchange.
  }
  if (currentUser) {
    // Honor redirect_to (or redirect) parameter if provided, else default to /chat
    try {
      const params = new URLSearchParams(location.search || '');
      const redirectTo = params.get('redirect_to') || params.get('redirect');
      if (!redirectTo) {
        try { const stored = localStorage.getItem('post_login_redirect'); if (stored) { return <Navigate to={stored} replace />; } } catch (_) { }
      }
      if (redirectTo && redirectTo.startsWith('/')) {
        if (redirectTo.startsWith('/market-portal')) {
          // Leave SPA and load the portal directly
          try { localStorage.removeItem('post_login_redirect'); } catch (_) { }
          if (typeof window !== 'undefined') {
            window.location.replace(redirectTo);
            return null;
          }
        }
        return <Navigate to={redirectTo} replace />;
      }
    } catch (e) { }
    return <Navigate to="/inbox" replace />;
  }
  return children;
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, loading: authLoading } = useContext(AuthContext);

  useEffect(() => {
    console.log(`[AppRoutes] Location Changed: ${location.pathname}${location.search}${location.hash}`, "currentUser:", currentUser ? currentUser.id : null, "authLoading:", authLoading);
  }, [location, currentUser, authLoading]);

  const handleLogout = useCallback(async () => {
    console.log("Attempting logout from AppRoutes...");
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Error signing out:", error);
    } else {
      console.log("Sign out successful, navigating to login.");
      navigate('/login');
    }
  }, [navigate]);

  // Post-login check for onboarding
  useEffect(() => {
    // If the user is on accept-invitation, or signup, or login, or other key public-utility paths,
    // absolutely do nothing in this effect regarding onboarding profile checks or redirects.
    // Let those pages handle their own logic. AcceptInvitationPage should redirect to signup.
    if (
      location.pathname === '/accept-invitation' ||
      location.pathname === '/signup' ||
      location.pathname === '/login' ||
      location.pathname === '/reset-password' ||
      location.pathname === '/forgot-password'
    ) {
      console.log(`[App.js Onboarding Effect] Path is ${location.pathname}, skipping all onboarding/profile logic contained in this effect.`);
      return;
    }

    if (currentUser && !authLoading) {
      const checkOnboardingStatus = async () => {
        console.log("[AppRoutes Onboarding] Authenticated user found, checking onboarding status...");

        // Add a small delay to mitigate race conditions right after sign-up
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
          const isInvitee = (() => {
            try { return localStorage.getItem('isInviteeSignup') === 'true'; } catch (_) { return false; }
          })();

          // Helper: wait for company_id to be set if user is an invitee
          const waitForCompanyId = async (userId, maxAttempts = 10, delayMs = 500) => {
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
              const { data: prof, error: profErr } = await supabase
                .from('user_profiles')
                .select('company_id')
                .eq('user_id', userId)
                .maybeSingle();
              if (profErr && profErr.code !== 'PGRST116') {
                console.error('[AppRoutes Onboarding] Error waiting for company_id:', profErr);
                break;
              }
              if (prof?.company_id) {
                return prof;
              }
              await new Promise(r => setTimeout(r, delayMs));
            }
            return null;
          };

          let { data: profile, error: profileError } = await supabase
            .from('user_profiles')
            .select('company_id')
            .eq('user_id', currentUser.id)
            .maybeSingle();

          if (profileError && profileError.code !== 'PGRST116') {
            console.error("Error fetching user profile for onboarding check:", profileError);
            return;
          }

          if (!profile) {
            if (isInvitee) {
              console.log('[AppRoutes Onboarding] No profile found but user is an invitee. Skipping placeholder profile creation to let invitation processing upsert it.');
            } else {
              console.log("No profile found for user, creating one for onboarding check...");
              const { data: newProfile, error: insertError } = await supabase
                .from('user_profiles')
                .insert({ user_id: currentUser.id, company_id: null })
                .select('company_id')
                .single();

              if (insertError) {
                console.error("Error creating user profile during onboarding check:", insertError);
                return;
              }
              profile = newProfile;
            }
          }

          const isOnboardingPath = location.pathname.startsWith('/onboarding');
          const isPricingPath = location.pathname.startsWith('/pricing') || location.pathname.startsWith('/payment');
          const allowPostBillingOnboarding = (() => {
            try { return localStorage.getItem('postBillingOnboarding') === '1'; } catch (_) { return false; }
          })();

          // If the user is an invitee, give the backend a moment to link the company
          if (isInvitee && !profile?.company_id) {
            console.log('[AppRoutes Onboarding] Invitee detected without company_id yet. Waiting for invitation processing...');
            const waitedProfile = await waitForCompanyId(currentUser.id, 10, 500);
            if (waitedProfile) {
              profile = waitedProfile;
              console.log('[AppRoutes Onboarding] Company_id detected after wait. Proceeding.');
            } else {
              console.log('[AppRoutes Onboarding] Still no company_id after waiting. Skipping onboarding redirect for invitee.');
              return; // Do not redirect invitees to onboarding
            }
          }

          // Scenario 1: User has no company yet. They must start the onboarding flow.
          if (!profile?.company_id) {
            if (!isOnboardingPath) {
              console.log("User has no company_id, redirecting to /onboarding/usage-choice.");
              navigate('/onboarding/usage-choice', { replace: true });
            }
            return; // Exit. User is either on the right path or has been redirected.
          }

          // From here, user HAS a company_id. Check for a subscription.
          const { data: subscription, error: subError } = await supabase
            .from('company_subscriptions')
            .select('status')
            .eq('company_id', profile.company_id)
            .eq('status', 'active')
            .maybeSingle();

          if (subError) {
            console.error("Error checking for active subscription:", subError);
            return; // Don't redirect on a database error
          }

          if (subscription) {
            // Scenario 2: Company has an active subscription. User is fully onboarded.
            // If they are on onboarding pages, allow post-billing profile onboarding pages when flagged.
            if (isPricingPath) {
              console.log("User's company has an active subscription and is on pricing. Redirecting to /inbox.");
              navigate('/inbox', { replace: true });
            } else if (isOnboardingPath) {
              const isProfileOnboarding = location.pathname.startsWith('/onboarding/profile');
              if (allowPostBillingOnboarding && isProfileOnboarding) {
                console.log("Post-billing onboarding flag present. Allowing onboarding/profile pages.");
              } else {
                console.log("User's company has an active subscription. Redirecting to /inbox.");
                navigate('/inbox', { replace: true });
              }
            }
          } else {
            // Scenario 3: Company exists, but has NO subscription. This is the admin onboarding flow.
            // If they are not on the pricing page, they should be.
            if (!isPricingPath) {
              console.log("User's company has no subscription. Redirecting to /pricing.");
              navigate('/pricing', { replace: true });
            }
          }
        } catch (e) {
          console.error("Error in post-login onboarding status check:", e);
        }
      };
      checkOnboardingStatus();
    }
  }, [currentUser, authLoading, navigate, location.pathname]);

  return (
    <div className="App">
      <Routes>
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

        {/* Public Routes */}
        <Route path="/" element={<PublicRoute><HomePage /></PublicRoute>} />
        <Route path="/demo/:slug" element={<DemoPage />} />
        <Route path="/demo-requested" element={<DemoRequestedPage />} />
        <Route path="/test_form" element={<TestFormPage />} />
        <Route path="/price" element={<PricingPublic />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/billing/success" element={<BillingSuccessLanding />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/post-billing" element={<PostBilling />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* WordPress Plugin OAuth Callback - Isolated route for WordPress integrations */}
        <Route path="/wordpress-oauth-callback" element={<WordPressOAuthCallback />} />

        {/* Private Onboarding Routes */}
        <Route path="/onboarding/profile" element={<PrivateRoute><OnboardingProfile /></PrivateRoute>} />
        <Route path="/onboarding/profile-2" element={<PrivateRoute><OnboardingPreferences /></PrivateRoute>} />
        <Route path="/onboarding/profile-3" element={<PrivateRoute><OnboardingWebsite /></PrivateRoute>} />
        <Route path="/onboarding/profile-4" element={<PrivateRoute><OnboardingGoals /></PrivateRoute>} />
        <Route path="/onboarding/profile-5" element={<PrivateRoute><OnboardingTrial /></PrivateRoute>} />
        <Route path="/onboarding/usage-choice" element={<PrivateRoute><UsageChoice /></PrivateRoute>} />
        <Route path="/onboarding/create-company" element={<PrivateRoute><CreateCompanyForm /></PrivateRoute>} />
        <Route path="/onboarding/create-team" element={<PrivateRoute><CreateTeamForm /></PrivateRoute>} />

        {/* Private Main Application Routes */}
        <Route path="/chat" element={<PrivateRoute><ChatInterface handleLogout={handleLogout} /></PrivateRoute>} />
        <Route path="/chat/:sessionId" element={<PrivateRoute><ChatInterface handleLogout={handleLogout} /></PrivateRoute>} />
        <Route path="/upload" element={<PrivateRoute><FileUpload handleLogout={handleLogout} /></PrivateRoute>} />
        <Route path="/files" element={<PrivateRoute><MyFilesPage /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
        <Route path="/upgrade" element={<PrivateRoute><UpgradePage /></PrivateRoute>} />
        <Route path="/billing" element={<PrivateRoute><BillingPage /></PrivateRoute>} />
        <Route path="/faq" element={<PrivateRoute><FAQManagement handleLogout={handleLogout} /></PrivateRoute>} />
        <Route path="/demo-admin" element={<PrivateRoute><DemoAdmin handleLogout={handleLogout} /></PrivateRoute>} />
        <Route path="/leads" element={<PrivateRoute><LeadManagementPage /></PrivateRoute>} />
        <Route path="/leads/linkedin" element={<PrivateRoute><LinkedInOutreachPage /></PrivateRoute>} />
        <Route path="/inbox" element={<PrivateRoute><AgentInbox handleLogout={handleLogout} /></PrivateRoute>} />
        <Route path="/widget-settings" element={<PrivateRoute><WidgetSettings /></PrivateRoute>} />
        <Route path="/channels" element={<PrivateRoute><ChannelsSettings /></PrivateRoute>} />


        {/* Private Admin Routes */}
        <Route path="/admin" element={<PrivateRoute><AdminPanel /></PrivateRoute>} />
        <Route path="/admin/integrations" element={<PrivateRoute><AdminPlatformsPage /></PrivateRoute>} />
        <Route path="/my-stores" element={<PrivateRoute><IntegrationsPage /></PrivateRoute>} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/admin/packages" element={<PrivateRoute><PackagesAdmin /></PrivateRoute>} />
        <Route path="/admin/live-agent" element={<PrivateRoute><LiveAgentAdmin /></PrivateRoute>} />
        <Route path="/admin/models" element={<PrivateRoute><AdminModels /></PrivateRoute>} />
        <Route path="/admin/embedding-models" element={<PrivateRoute><AdminEmbeddingModels /></PrivateRoute>} />
        <Route path="/admin/system-defaults" element={<PrivateRoute><AdminSystemDefaults /></PrivateRoute>} />
        <Route path="/admin/system-health" element={<PrivateRoute><SystemHealthPage /></PrivateRoute>} />
        <Route path="/admin/notifications" element={<PrivateRoute><AdminNotificationsPage /></PrivateRoute>} />
        <Route path="/admin/user/:userId" element={<PrivateRoute><UserDetailPage /></PrivateRoute>} />
        <Route path="/admin/user/:userId/files" element={<PrivateRoute><UserFilesPage /></PrivateRoute>} />
        <Route path="/admin/user/:userId/settings" element={<PrivateRoute><UserSettingsAdminPage /></PrivateRoute>} />
        <Route path="/admin/user/:userId/session-settings" element={<PrivateRoute><SessionSettingsPage /></PrivateRoute>} />
        <Route path="/admin/user/:userId/default-settings" element={<PrivateRoute><UserSettingsPage /></PrivateRoute>} />
        <Route path="/admin/user/:userId/integrations" element={<PrivateRoute><UserIntegrationsPage /></PrivateRoute>} />

        {/* Fallback for any other path */}
        <Route
          path="*"
          element={
            currentUser && !authLoading
              ? <Navigate to="/inbox" replace />
              : <Navigate to="/login" replace />
          }
        />
      </Routes>
    </div>
  );
}

function App() {
  React.useEffect(() => {
    async function loadBuckets() {
      const { data, error } = await supabase.storage.listBuckets()
      console.log('buckets data:', data, 'error:', error)
    }
    loadBuckets()
  }, [])

  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;